import { lte } from 'lodash';
import api from '../services/axios.service';
import { constructQueryParams } from '../utils/utils';

export async function fetchTransformsForConnectors(page_size = 1000) {
    return await api.get(`transforms?page_size=${page_size}`);
}

export async function fetchTransform(id) {
    const response = await api.get(`transforms/${id}`);

    return response?.result?.[0];
}

export async function fetchTransforms({ page_size, page_number, partial_name }) {
    const queryParams = constructQueryParams({ page: page_number, page_size, partial_name });

    return await api.get(`transforms/details?${queryParams}`);
}

export async function fetchTransformConfigs() {
    return api.get(`transforms/configs`);
}

export async function editTransform(data) {
    return api.put(`transforms`, data);
}

export async function createTransform(data) {
    return api.post(`transforms`, { ...data, created_from: 'web' });
}

export async function cloneTransform(transformId, name = null) {
    const payload = name ? { name } : {};
    return api.post(`transforms/${transformId}/clone`, payload);
}

export async function deleteTransform(id) {
    return api.delete(`transforms?id=${id}`);
}

export async function transformImplementationDetails(id) {
    return api.get(`transforms/${id}/implementation_details`);
}

export async function editImplementationDetails(id, data) {
    return api.put(`transforms/${id}/implementation_details`, data);
}

/** @returns {Array<string>} */
export async function topicsForPattern(topicPattern, replacementPattern) {
    var topicPatternEsc = encodeURIComponent(topicPattern);
    var replacementPatternEsc = encodeURIComponent(replacementPattern);
    return api.get(`transforms/topics-for-pattern/${topicPatternEsc}/${replacementPatternEsc}`);
}

/** @returns {Array<string>} */
export async function clearOutputTopics(id, outputTopicPattern) {
    var outputTopicPatternEsc = encodeURIComponent(outputTopicPattern);
    return api.put(`transforms/${id}/clear-output-topics/${outputTopicPatternEsc}`);
}

export async function runUnitTests(id, versionId) {
    return api.put(`transforms/${id}/run-unit-tests/${versionId}`);
}

export async function deployPreview(id, versionId, replayWindow) {
    return api.put(`transforms/${id}/deploy-job-preview/${versionId}?replay_window=${encodeURIComponent(replayWindow)}`);
}

export async function deployLive(id, versionId) {
    return api.put(`transforms/${id}/deploy-job-live/${versionId}`);
}

/**
 * Enum for tri-state values.
 * @readonly
 * @enum {string}
 */
let TransformStatus = {
    RUNNING: 'RUNNING',
    CANCELLED: 'CANCELLED',
    FAILED: 'FAILED'
};

/**
 * A point on a two dimensional plane.
 * @typedef {Object} TransformJobStatusRes
 * @property {string} id The job id
 * @property {string} job_name The job name
 * @property {TransformStatus} status The flink status
 */

/** @returns {Promise<TransformJobStatusRes>} */
export async function getJobStatus(id, showToaster = true) {
    return await api.get(`transforms/${id}/job_status`, {}, !showToaster);
}

/** @returns {Array<string>} */
export async function getOutputTopicsForTransform(id, showToaster = true) {
    return await api.get(`transforms/${id}/output-topics`, {}, !showToaster);
}

export async function setInputOutputTopic(id, topicPattern, replacementPattern) {
    var topicPatternEsc = encodeURIComponent(topicPattern);
    var replacementPatternEsc = encodeURIComponent(replacementPattern);
    return api.put(`transforms/${id}/set-input-output-topics/${topicPatternEsc}/${replacementPatternEsc}`);
}

/**
 * A point on a two dimensional plane.
 * @typedef {Object} TransformJobError
 * @property {string} job_id The job id
 * @property {string} message
 + @property {string} stacktrace
 */

/** @returns {Array<TransformJobError>} */
export async function getErrorsForTransform(id, showToaster = true) {
    return await api.get(`transforms/${id}/errors`, {}, !showToaster);
}

/**
 * DLQ (Dead Letter Queue) error information
 * @typedef {Object} DlqError
 * @property {string} timestamp - ISO timestamp of the error
 * @property {string} error_message - Error message from DLQ headers
 * @property {number} message_offset - Message offset in DLQ topic
 * @property {string} dlq_topic_name - DLQ topic name
 */

/** @returns {Promise<DlqError|null>} */
export async function getDlqErrorsForTransform(id, showToaster = true) {
    return await api.get(`transforms/${id}/dlq`, {}, !showToaster);
}
